import { RpcError } from '@protobuf-ts/runtime-rpc'
import { ClosableProps } from 'components/no-ui/context/modal-provider'
import { BaseActionForm } from 'components/pages/task/action/form/BaseActionForm'
import { getRemovePresets } from 'components/pages/task/action/messages'
import { useAlert } from 'hooks/useAlert/index'
import { TaskValue } from 'lib/actions/task'
import { ReviewPendingDecision } from 'pbts/c1/api/accessreview/v1/accessreview_pending'
import { FC, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { usePendingTicketActionTracker } from 'redux_toolkit/lib/PendingTicketActionTracker'

type DenyWithCommentPendingActionDialogProps = {
    task: TaskValue
} & ClosableProps

export const DenyWithCommentPendingActionDialog: FC<DenyWithCommentPendingActionDialogProps> = ({
    task,
    close,
}) => {
    const intl = useIntl()
    const toast = useAlert()
    const pendingTicketActionTracker = usePendingTicketActionTracker()

    const handleSave = useCallback(
        async (comment: string): Promise<void> => {
            if (!pendingTicketActionTracker) return

            try {
                await pendingTicketActionTracker.mutatePendingAction(task, ReviewPendingDecision.DENY, comment)
            } catch (err: unknown) {
                toast(
                    intl.formatMessage(
                        {
                            id: 'pending_action_deny_with_comment_error',
                            defaultMessage: 'Error queuing denial {code}: {msg}',
                        },
                        {
                            code: (err as RpcError).code,
                            msg: (err as RpcError).message,
                        },
                    ),
                    'error',
                )
                return
            }

            toast(
                intl.formatMessage({
                    id: 'pending_action_deny_queued',
                    defaultMessage: 'Denial queued',
                }),
                'success',
            )
            close()
        },
        [pendingTicketActionTracker, task, intl, toast, close],
    )

    return <BaseActionForm
        closeDialog={close}
        open
        onDeny={handleSave}
        title={intl.formatMessage({
            id: 'component_task_action_form_remove_pending_title',
            defaultMessage: 'Queue removal with comment',
        })}
        description={intl.formatMessage({
            id: 'component_action_form_remove_pending_description',
            defaultMessage: 'Add your comment to let others know why you recommend removing this access.',
        })}
        presets={getRemovePresets(intl)}
        actionText={intl.formatMessage({
            id: 'pending_action_queue_denial',
            defaultMessage: 'Deny',
        })}
    />
}
